'use client'

import { Button, Checkbox, Input, LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { FC, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import TwoColumnsWithBackgroundImage from '@/components/TwoColumnsWithBackgroundImage'
import { useNewsletter } from '@/hooks/useNewsLetter'

// eslint-disable-next-line complexity
const DownloadGuide: FC<{ hubspotFormId: string; purpleVersion?: boolean }> = ({
    hubspotFormId,
    purpleVersion,
}) => {
    const { palette } = LuneTheme
    const { smScreen, xsScreen, mdScreen } = useDevice()
    const { isValidEmail, toHubspot } = useNewsletter(hubspotFormId, [
        'Access our full project evaluation process.',
        'I am happy to receive marketing communications from Lune.',
    ])

    const [name, setName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [company, setCompany] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [lastNameError, setLastNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [companyError, setCompanyError] = useState<boolean>(false)
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>()
    const [debouncedEmail] = useDebounce(email, 1000)

    useEffect(() => {
        if (isValidEmail(email) || email === '') {
            setEmailError(false)
        } else {
            setEmailError(true)
        }
    }, [debouncedEmail, email, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    useEffect(() => {
        setLastNameError(false)
    }, [lastName])

    const onSubmit = async () => {
        const validName = name.length
        const validLastName = lastName.length
        const validEmail = isValidEmail(email)
        const validCompany = company.length

        if (!validName || !validEmail || !validCompany) {
            !validName && setNameError(true)
            !validLastName && setLastNameError(true)
            !validEmail && setEmailError(true)
            !validCompany && setCompanyError(true)
            return
        }

        const data = await toHubspot({
            firstname: name,
            lastname: lastName,
            email,
            company,
            legalConsent: marketingConsent ? 'marketing' : 'gatedContent',
        })
        if (data.status === 'error') {
            setSuccess(false)
        } else {
            setSuccess(true)
            setName('')
            setLastName('')
            setEmail('')
            setCompany('')
            setMarketingConsent(false)
        }
    }

    return (
        <TwoColumnsWithBackgroundImage
            backgroundImage={
                purpleVersion
                    ? '/images/customer-quotes/greening-supply-chain-desktop.png'
                    : '/images/customer-quotes/quality-over-quantity-desktop.png'
            }
            mobileBackgroundImage={
                purpleVersion
                    ? '/images/customer-quotes/greening-supply-chain-mobile.png'
                    : '/images/customer-quotes/quality-over-quantity-mobile.png'
            }
            rightSide={<Box sx={{ height: smScreen ? '468px' : xsScreen ? '516px' : 'auto' }} />}
            leftSide={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        gap: mdScreen ? 3 : 4,
                    }}
                >
                    {success ? (
                        <Box
                            sx={{
                                height: mdScreen ? '508px' : '572px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: mdScreen ? 4 : 9,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                Thank you! <br />
                                <br />
                                Your free guide is on its way to your inbox right now.
                            </Text>
                        </Box>
                    ) : (
                        <>
                            <Text variant={'button'}>FREE GUIDE</Text>
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                {purpleVersion ? (
                                    <>
                                        Greening the supply
                                        <br />
                                        chain
                                    </>
                                ) : (
                                    <>
                                        Quality over quantity:
                                        <br />
                                        how we evaluate carbon projects at Lune
                                    </>
                                )}
                            </Text>
                            <Text variant={mdScreen ? 'body2' : 'body1'}>
                                {purpleVersion ? (
                                    <>
                                        Explore the environmental impact of supply chains,
                                        investigate the push and pull factors changing the logistic
                                        landscape, and strategies sustainability leaders are already
                                        implementing to reach net zero.
                                    </>
                                ) : (
                                    <>
                                        Explore our rigorous internal analysis, alignment with
                                        external frameworks like BeZero ratings and Oxford
                                        Offsetting Principles, and comprehensive six-point
                                        evaluation criteria.
                                    </>
                                )}
                            </Text>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: '100%',
                                    '.MuiInputBase-root ': {
                                        width: '100%',
                                    },
                                }}
                            >
                                <Input
                                    placeholder={'Name'}
                                    error={nameError}
                                    value={name}
                                    sx={{ backgroundColor: palette.White }}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <Input
                                    placeholder={'Last name'}
                                    error={lastNameError}
                                    value={lastName}
                                    sx={{ backgroundColor: palette.White }}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                                <Input
                                    placeholder={'Company name'}
                                    error={companyError}
                                    value={company}
                                    sx={{ backgroundColor: palette.White }}
                                    onChange={(e) => setCompany(e.target.value)}
                                />
                                <Input
                                    placeholder={'Email'}
                                    error={emailError}
                                    value={email}
                                    sx={{ backgroundColor: palette.White }}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <Box pt={4}>
                                    <Checkbox
                                        sx={{ marginRight: '12px' }}
                                        checked={marketingConsent}
                                        onChange={(e) => setMarketingConsent(e.target.checked)}
                                    />

                                    <Text
                                        variant="body3"
                                        onClick={() => setMarketingConsent(!marketingConsent)}
                                        sx={{ '&:hover': { cursor: 'pointer' } }}
                                    >
                                        I am happy to receive marketing communications from Lune
                                    </Text>
                                </Box>
                            </Box>
                            <Button
                                sx={{
                                    width: {
                                        xs: '100%',
                                        sm: '100%',
                                        md: 'initial',
                                        lg: 'initial',
                                        xl: 'initial',
                                    },
                                }}
                                onClick={onSubmit}
                                leftIcon={<FileDownloadOutlinedIcon />}
                            >
                                Download guide
                            </Button>
                        </>
                    )}
                </Box>
            }
            reversedOnMobile={true}
        />
    )
}

export default DownloadGuide
